/**
 * @file.
 * Contains Account service.
 */
import { AccountUtils } from 'common/utils/account.util';
import {
  ActivatePSPError,
  captureError,
  NotFoundAccountError,
  PaymentError,
} from 'common/utils/error.util';
import { cachedGet, TradingService } from '../../BackEnd/API';
import { _ExnessAccount } from '../stores/accounts/exness-account.store';

class _AccountService {
  getPSPList = async ({ type, id }) => {
    return TradingService.get(
      `/api/v1/fxtr/application/guid/user/platform/trading/accounts/${type}/${id}/psp-list`
    );
  };

  updateAccount = async (accountID, data) => {
    return TradingService.put(`/api/v1/fxtr/account/${accountID}`, data);
  };

  getAccountOperations = (accountID, params) => {
    return TradingService.get(`/api/v1/fxtr/account/${accountID}/trade`, {
      params,
    });
  };

  createSession = (data) => TradingService.post('/api/v1/fxtr/session', data);

  getSession = (token) => TradingService.get(`/api/v1/fxtr/session/${token}`);

  makeDeposit = (data) =>
    TradingService.post('/api/v1/fxtr/deposit', data).catch((error) => {
      captureError(new PaymentError(error));
      throw error;
    });

  getOperations = ({ offset, limit, from, to, search, istrading = true }) => {
    let params = `offset=${offset}&limit=${limit}`;
    if (from && to) {
      params += `&from=${from}&to=${to}`;
    }
    if (search) {
      params += `&search=${search}`;
    }
    params += `&istrading=${istrading}`;
    return TradingService.get(`/api/v1/fxtr/balance_transaction?${params}`);
  };

  getTransaction = (guid) =>
    TradingService.get(`/api/v1/fxtr/balance_transaction/${guid}`);

  getAccountFinDetails = ({ id, type }) =>
    TradingService.get(
      `/api/v1/fxtr/application/guid/user/platform/trading/accounts/${type}/${id}/fin-stat`
    );

  makeWithdrawal = (data) =>
    TradingService.post('/api/v1/fxtr/withdrawal', data);

  getAccountId = ({ id, type }) =>
    TradingService.get(
      `/api/v1/fxtr/application/guid/user/platform/trading/accounts/${type}/${id}/get-id`
    );

  getAccount = ({ id, type }) =>
    TradingService.get(
      `/api/v1/fxtr/application/guid/user/platform/trading/accounts/${type}/${id}`
    );

  getAccounts = () =>
    TradingService.get(
      '/api/v1/fxtr/application/guid/user/platform/trading/accounts'
    );

  activatePSP = ({ pspGuid, accountNumber }) =>
    TradingService.post(`/api/v1/fxtr/psp/${pspGuid}/${accountNumber}`).catch(
      (error) => {
        captureError(new ActivatePSPError(error));
        throw error;
      }
    );

  getBonuses = (accId) =>
    TradingService.get(`/api/v1/fxtr/account/${accId}/bonus`);

  getPaginatedAccounts = async ({
    limit,
    offset,
    servername,
    isArchived,
    sort,
    isAsc = true,
    istrading = true,
  }) => {
    let params = `offset=${offset}&limit=${limit}&isarchived=${Boolean(
      isArchived
    )}&istrading=${istrading}`;
    if (sort) {
      params += `&sort=${sort}`;
      params += `&isasc=${isAsc}`;
    }

    return TradingService.get(`/api/v1/fxtr/accounts/${servername}?${params}`);
  };

  getAllAccounts = async function (params) {
    const archivedParams = {
      ...params,
      isArchived: true,
    };
    const activeParams = {
      ...params,
      isArchived: false,
    };

    const handleRequest = async (callback, type) =>
      callback().catch((e) => {
        console.error(`error getting [${type}] accounts`, e);
        return [];
      });

    const prepare = (data) => data.map((acc) => new _ExnessAccount(acc));

    const active = await handleRequest(
      () => this.getPaginatedAccounts(activeParams),
      'active'
    );

    const archived = await handleRequest(
      () => this.getPaginatedAccounts(archivedParams),
      'archived'
    );

    const all = [].concat(archived.data, active.data);

    return Promise.resolve(prepare(all));
  };

  createAccount = (data) => TradingService.post('/api/v1/fxtr/account', data);

  getServers = async () => await TradingService.get('/api/v1/fxtr/servers');

  getProfile = ({ page }) =>
    TradingService.get(
      `/api/v1/fxtr/profile?redirect=${
        page.includes('localhost') ? 'https://stage-pa.rock-west.net' : page
      }`
    );

  saveEditMasterAccount = (body, masterId) =>
    TradingService.patch(`/api/v1/fxtr/copy-trading/master/${masterId}`, {
      ...body,
    });

  saveEditInvestorAccount = (body, investorId) =>
    TradingService.patch(`/api/v1/fxtr/copy-trading/investor/${investorId}`, {
      ...body,
    });

  createMasterAccount = (body) =>
    TradingService.post('/api/v1/fxtr/copy-trading/master', { ...body });

  createInvestorAccount = (body) =>
    TradingService.post('/api/v1/fxtr/copy-trading/investor', { ...body });

  updateProfile = (profile) =>
    TradingService.put('/api/v1/fxtr/profile', profile);

  deletePhone = (id) =>
    TradingService.delete(`/api/v1/fxtr/profile/contact/phone/${id}`);

  makePhonePrimary = (id) =>
    TradingService.put(`/api/v1/fxtr/profile/contact/phone/${id}`);

  makeEmailPrimary = (id) =>
    TradingService.put(`/api/v1/fxtr/profile/contact/mail/${id}`);

  deleteEmail = (id) =>
    TradingService.delete(`/api/v1/fxtr/profile/contact/mail/${id}`);

  deleteSocial = (id) =>
    TradingService.delete(`/api/v1/fxtr/profile/contact/social/${id}`);

  getPosition = (accountID, positionID) =>
    cachedGet(
      TradingService,
      `/api/v1/fxtr/account/${accountID}/position/${positionID}`
    );

  deleteAccount = ({ type, accountId }) =>
    TradingService.delete(
      `/api/v1/fxtr/application/guid/user/platform/trading/accounts/${type}/${accountId}`
    );
}

export const AccountService = new _AccountService();
